'use client';

import Image from 'next/image';
import { useEffect, useState } from 'react';
import { ctaClick } from '@/lib/analytics';
import { setFormSource } from '@/lib/formSource';
import * as gtag from '@/lib/gtag';
import type { Language } from '@/lib/translations';

/** Identifies this promotion in GA4, including on the form it sends people to. */
const PROMO_ID = 'promo_popup_free_trial';

export default function PromoPopup({ language }: { language: Language }) {
  const [visible, setVisible] = useState(false);

  // Shown on every page load. Closing it only hides it for this page view,
  // so the promotion comes back as the visitor moves through the site.
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setVisible(true);
      gtag.event('promo_impression', {
        event_category: 'Promotion',
        event_label: PROMO_ID,
        custom_parameters: {
          promotion_id: PROMO_ID,
          language,
          page_path: window.location.pathname,
        },
      });
    }, 2000);
    return () => window.clearTimeout(timer);
  }, [language]);

  const close = () => setVisible(false);

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    // Attribute the form's own GA4 events back to this promotion.
    setFormSource(PROMO_ID);

    gtag.event('promo_click', {
      event_category: 'Promotion',
      event_label: PROMO_ID,
      custom_parameters: {
        promotion_id: PROMO_ID,
        language,
        page_path: window.location.pathname,
      },
    });
    const formSection = document.getElementById('contact-form');
    ctaClick(
      PROMO_ID,
      'popup',
      language,
      formSection ? '#contact-form' : event.currentTarget.href
    );
    if (formSection) {
      event.preventDefault();
      formSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      window.setTimeout(() => {
        document.getElementById('brandName')?.focus();
      }, 500);
    }
    close();
  };

  if (!visible) return null;

  const label =
    language === 'en'
      ? 'Free trial — book a consultation'
      : '免費試用，立即預約諮詢';

  return (
    <div className="fixed bottom-3 left-3 z-40 w-[160px] sm:bottom-6 sm:left-6 sm:w-[220px]">
      <div className="relative overflow-hidden rounded-2xl bg-white shadow-xl ring-1 ring-black/10 dark:bg-gray-900 dark:ring-white/10">
        <a
          href={`/${language}/contact/`}
          onClick={handleClick}
          aria-label={label}
          className="block focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <Image
            src={`/assets/images/promos/free-trial-${language}.webp`}
            alt={label}
            width={640}
            height={640}
            className="h-auto w-full"
          />
        </a>
        <button
          type="button"
          onClick={close}
          aria-label={language === 'en' ? 'Close' : '關閉'}
          className="absolute right-2 top-2 grid h-7 w-7 cursor-pointer place-items-center rounded-full bg-white/85 text-gray-600 shadow transition-colors hover:bg-white hover:text-gray-900"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            className="h-4 w-4"
            aria-hidden="true"
          >
            <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
          </svg>
        </button>
      </div>
    </div>
  );
}
